import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp, Button } from 'antd';
import SheetSelect from '../components/SheetSelect';
import AppBar from '../components/AppBar';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';
import { num } from '../lib/loanCalc';

// Refinance — current mortgage vs a new facility on today's market value.
// New loan = market price × margin; costs use the same statutory scales as the
// Mortgage tool plus the lock-in penalty on the outstanding amount.

const DEFAULTS = {
  currentRepayment: '', outstanding: '', lockInPct: '2.0',
  marketPrice: '', marginPct: '90', ratePct: '4.00', years: '30',
};

const LOCK_IN_OPTS = ['0', '0.5', '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '5.0'].map((v) => ({ value: v, label: `${v}%` }));
const MARGIN_OPTS = ['60', '65', '70', '75', '80', '85', '90', '95', '100'].map((v) => ({ value: v, label: `${v}%` }));

export default function RefinancePage() {
  const nav = useNavigate();
  const { modal, message } = AntApp.useApp();
  const [f, setF] = useState(DEFAULTS);
  const patch = (p) => setF((x) => ({ ...x, ...p }));

  const info = () =>
    modal.info({
      title: 'Refinance',
      content: 'Mortgage refinancing is a facility offered by financial institutions that may help to reduce monthly loan repayments, lower the interest rate, consolidate outstanding loans, or cash out. It usually takes 3 to 6 months for processing, depending on the type of the property. You might incur costs such as valuation fees, legal fees, stamp duty and lock-in charges — though some financial institutions absorb these costs if you fit their criteria.',
      okText: 'OK',
    });

  const calculate = () => {
    if (!(num(f.outstanding) > 0)) { message.warning('Enter the current outstanding amount'); return; }
    if (!(num(f.marketPrice) > 0)) { message.warning('Enter the estimated market price'); return; }
    if (!(num(f.ratePct) > 0) || !(num(f.years) > 0)) { message.warning('Enter the new interest rate and tenure'); return; }
    nav('/tools/refinance/result', { state: { f } });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Refinance" right={<InfoDot onClick={info} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Section title="CURRENT MORTGAGE DETAILS">
          <Field label="Current Monthly Repayment">
            <MoneyInput value={f.currentRepayment} onChange={(v) => patch({ currentRepayment: v })} placeholder="0" />
          </Field>
          <Field label="Current Outstanding Amount">
            <MoneyInput value={f.outstanding} onChange={(v) => patch({ outstanding: v })} placeholder="0" />
          </Field>
          <Field label="Lock-in Penalty (%)">
            <SheetSelect title="Lock-in penalty (%)" value={f.lockInPct} onChange={(v) => patch({ lockInPct: v })} options={LOCK_IN_OPTS} />
          </Field>
        </Section>

        <Section title="NEW MORTGAGE DETAILS">
          <Field label="Estimated Market Price">
            <MoneyInput value={f.marketPrice} onChange={(v) => patch({ marketPrice: v })} placeholder="0" />
          </Field>
          <Field label="Loan Margin (%)">
            <SheetSelect title="Loan margin (%)" value={f.marginPct} onChange={(v) => patch({ marginPct: v })} options={MARGIN_OPTS} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Interest Rate (%)">
              <NumInput value={f.ratePct} onChange={(v) => patch({ ratePct: v })} placeholder="4.00" />
            </Field>
            <Field label="Loan Tenure (Year)">
              <NumInput value={f.years} onChange={(v) => patch({ years: v })} placeholder="30" />
            </Field>
          </div>
        </Section>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <Button block size="large" onClick={() => setF(DEFAULTS)}>Reset</Button>
        <Button block size="large" type="primary" onClick={calculate}>Calculate</Button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--primary)' }}>{title}</div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);
